import React, { useState } from 'react';
import '../css/FAQPage.css';
import { ChevronDown, MessageCircle, Mail, Phone } from 'lucide-react';

interface FAQItem {
  question: React.ReactNode;
  answer: React.ReactNode;
  category: string;
}

const FAQPage: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('All');

  const faqData: FAQItem[] = [
    {
      category: 'General',
      question: <span>What is Crikey<sup>ai</sup>?</span>,
      answer: <span>Crikey<sup>ai</sup> is an advanced artificial intelligence platform designed specifically for insurance claims processing. Our AI agents improve efficiency, accuracy, and the claim experience by automating routine tasks and providing intelligent insights for faster, more accurate claims decisions.</span>
    },
    {
      category: 'General',
      question: <span>How does Crikey<sup>ai</sup> work?</span>,
      answer: <span>Crikey<sup>ai</sup> uses advanced machine learning algorithms to analyze claims data, extract relevant information, and provide intelligent recommendations. Our system integrates seamlessly with your existing workflow, learning from your historical data to continuously improve accuracy and efficiency.</span>
    },
    {
      category: 'General',
      question: 'What types of insurance claims does Crikey support?',
      answer: <span>Crikey<sup>ai</sup> supports a wide range of insurance claims including property, casualty, health, auto, and commercial lines. Our platform is customizable to handle specialized claim types specific to your organization.</span>
    },
    /* {
      category: 'Pricing',
      question: <span>How much does Crikey<sup>ai</sup> cost?</span>,
      answer: 'We offer three pricing tiers: Starter ($499/month), Professional ($1,299/month), and Enterprise (custom pricing). All plans include a 14-day free trial. Annual billing provides a 20% discount. Visit our Pricing page for detailed feature comparisons.'
    },
    {
      category: 'Pricing',
      question: 'Is there a free trial available?',
      answer: 'Yes! All plans include a 14-day free trial with full access to features. No credit card required to start your trial. You can cancel anytime during the trial period with no charges.'
    },
    {
      category: 'Pricing',
      question: 'Can I change my plan later?',
      answer: 'Absolutely! You can upgrade or downgrade your plan at any time. Changes take effect immediately, and we\'ll prorate any adjustments to your billing.'
    }, */
    {
      category: 'Technical',
      question: <span>How do I integrate Crikey<sup>ai</sup> with my existing systems?</span>,
      answer: <span>Crikey<sup>ai</sup> offers multiple integration options including REST APIs, webhooks, and pre-built connectors for popular insurance management systems. Our technical team provides full onboarding support, and Enterprise plans include dedicated integration assistance.</span>
    },
    {
      category: 'Technical',
      question: 'Is my data secure?',
      answer: 'Security is our top priority. We use enterprise-grade encryption (AES-256) for data at rest and in transit, maintain SOC 2 Type II compliance, and follow industry best practices. Your data is stored in secure, geographically distributed data centers with regular backups.'
    },
    {
      category: 'Technical',
      question: 'What is the uptime guarantee?',
      answer: 'We maintain 99.9% uptime across all plans. Enterprise customers receive a 99.95% SLA guarantee with financial credits for any downtime exceeding our commitment.'
    },
    {
      category: 'Technical',
      question: <span>Can Crikey<sup>ai</sup> be deployed on-premise?</span>,
      answer: 'Yes, on-premise deployment is available for Enterprise customers. We also offer hybrid cloud solutions that combine the benefits of cloud flexibility with on-premise security requirements.'
    },
    {
      category: 'Support',
      question: 'What kind of support do you offer?',
      answer: 'Support varies by plan: Starter includes email support with 24-hour response time, Professional adds priority chat support with 4-hour response, and Enterprise includes a dedicated account manager with phone support and 1-hour response time.'
    },
    {
      category: 'Support',
      question: 'Do you offer training for my team?',
      answer: 'Yes! All plans include access to our comprehensive knowledge base and video tutorials. Professional and Enterprise plans include live onboarding sessions, and Enterprise customers receive customized training programs for their teams.'
    },
    {
      category: 'Features',
      question: 'How accurate is the AI analysis?',
      answer: 'Our AI maintains an average accuracy rate of 95%+ for claims analysis. The system continuously learns from your specific data patterns, and accuracy typically improves to 98%+ within the first 90 days of use.'
    },
    {
      category: 'Features',
      question: 'Can I customize the AI workflows?',
      answer: 'Professional and Enterprise plans include custom workflow capabilities. You can define rules, set approval thresholds, and create automated routing based on your specific business requirements.'
    },
    {
      category: 'Features',
      question: <span>Does Crikey<sup>ai</sup> replace human claims agents?</span>,
      answer: <span>No, Crikey<sup>ai</sup> is designed to augment and empower claims agents, not replace them. Our AI handles routine tasks and provides intelligent recommendations, allowing claims agents to focus on complex cases and strategic decision-making.</span>
    }
  ];

  const categories = ['All', ...Array.from(new Set(faqData.map(item => item.category)))];

  const filteredFAQs = activeCategory === 'All' 
    ? faqData 
    : faqData.filter(item => item.category === activeCategory);

  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="faq-section">
      <div className="faq-content">
        <h2 className="faq-title">Frequently Asked Questions</h2>
        <p className="faq-subtitle">
          <span>Everything you need to know about Crikey<sup>ai</sup>. Can't find what you're looking for? Contact our support team.</span>
        </p>

        {/* Category Filter */}
        <div className="category-filter">
          {categories.map((category) => (
            <button
              key={category}
              className={`category-button ${activeCategory === category ? 'active' : ''}`}
              onClick={() => setActiveCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        {/* FAQ Accordion */}
        <div className="faq-accordion">
          {filteredFAQs.map((item, index) => (
            <div
              key={index}
              className={`faq-item ${openIndex === index ? 'open' : ''}`}
            >
              <button
                className="faq-question"
                onClick={() => toggleAccordion(index)}
              >
                <span className="question-text">{item.question}</span>
                <ChevronDown 
                  className={`chevron-icon ${openIndex === index ? 'rotated' : ''}`} 
                  size={24} 
                />
              </button>
              <div className={`faq-answer ${openIndex === index ? 'expanded' : ''}`}>
                <p>{item.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FAQPage;